import { HtmlContentModal, ModalWrapper, getSubstanceNames, useHtmlContentModal } from '@common/components';
import Badge from '@common/components/Badge/Badge';
import { Button } from '@common/components/Button/Button';
import { ButtonTypes } from '@common/components/Button/types';
import { IProductTableCallbacks } from '@common/components/DatapharmTable/types';
import IconWithTooltip from '@common/components/IconWithTooltip/IconWithTooltip';
import { Item } from '@common/components/NextStepsModal/components/Item';
import { NotifyModal } from '@common/components/NotifyModal';
import { ModalTypes } from '@common/components/NotifyModal/NotifyModal';
import { FileError } from '@common/components/NotifyModal/component/FileError';
import Tabs from '@common/components/Tabs/Tabs';
import { useUnsavedChanges } from '@common/context/hooks/useUnsavedChanges';
import { useFetchCompanyConfiguration } from '@common/features/configuration/useFetchCompanyConfiguration';
import { useRetireContentModal } from '@common/features/document/components/RetireModal/hooks/useRetireContentModal';
import { RetireModal } from '@common/features/document/components/RetireModal/RetireModal';
import { default as ActiveIngredientsTab } from '@common/features/productGroup/components/ActiveIngredients/ActiveIngredientsTab';
import { useProductGroupForm } from '@common/features/productGroup/hooks/useProductGroupForm';
import { queryKey } from '@common/features/submission/hooks/useFetchSubmission/useFetchSubmission';
import { ISubmissionDto, ProductChangeType, ProductStatus } from '@common/features/submission/types';
import { ValidateSubmissionErrors } from '@common/features/submission/ValidateSubmissionErrors/ValidateSubmissionErrors';
import { handleHelpClick } from '@common/helpers';
import { useFetchTasks } from '@common/hooks/useFetchTasks';
import { DocumentStatus, DocumentType, ISelectOption } from '@common/types';
import DatapharmTable from '@components/DatapharmTable/DatapharmTable';
import { DiscontinueProductModal } from '@components/DiscontinueProductModal';
import { useDiscontinueProductModal } from '@components/DiscontinueProductModal/useDiscontinueProductModal';
import { NextStepsModal } from '@components/NextStepsModal';
import { useNextStepsModal } from '@components/NextStepsModal/useNextStepsModal';
import NoContentPanel from '@components/NoContentPanel/NoContentPanel';
import { ReinstatementProductModal } from '@components/RetainmentProductModal';
import { useReinstatementProductModal } from '@components/RetainmentProductModal/useReinstatementProductModal';
import { RetireProductModal } from '@components/RetireProductModal';
import { useRetireProductModal } from '@components/RetireProductModal/useRetireProductModal';
import { HeaderBreadcrumb, Link } from '@components/SubmissionFlowHeader/styles';
import { useCancelModal } from '@hooks/useCancelModal';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useParams, useLocation } from 'react-router';
import AddDocumentModal from '../../../../components/AddDocumentModal/AddDocumentModal';
import { useAddDocumentModal } from '../../../../components/AddDocumentModal/hooks/useAddDocumentModal';
import AddProductPopup from '../../../../components/Product/AddProductPopup/AddProductPopup';
import { useRemoveModal } from '../../../../components/RemoveModal/hooks/useRemoveModal';
import RemoveModal from '../../../../components/RemoveModal/RemoveModal';
import SubmissionFlowHeader from '../../../../components/SubmissionFlowHeader/SubmissionFlowHeader';
import useAppNavigation, { BackToContentTypeState } from '../../../../routing/useAppNavigation';
import { SubmissionUpdateCommand } from '../../../submission/types';
import { mapDocumentsAndTasks, mapProductGroup, prepareProductsColumns } from '../../helpers';
import { useAddProduct } from '../../hooks/useAddProduct';
import { useMaHolder } from '../../hooks/useMaHolder';
import { usePrepareDocumentForRetire } from '../../hooks/usePrepareDocumentForRetire';
import { useSubmissionSummary } from '../../hooks/useSubmissionSummary';
import { SubmissionConfirmationType } from '../../hooks/useSubmissionSummary/useSubmissionSummary';
import { useUpdateSubmission } from '../../hooks/useUpdateSubmission';
import { useValidateSubmissionModal } from '../../hooks/useValidateSubmissionModal';
import { ProductGroupTabs } from '../../types';
import { CancelCreateSubmissionModal } from '../CancelCreateSubmissionModal/CancelCreateSubmissionModal';
import { DuplicateProductError, DuplicateProductErrorMode } from '@common/components/DuplicateProductError';
import SubmissionSummary from '../SubmissionSummary/SubmissionSummary';
import { default as ContentTab } from '../tabs/Content/Content';
import MaHolderTab from '../tabs/MaHolder/MaHolder';
import ProductTypeTab from '../tabs/ProductType/ProductTypeTab';
import * as Styled from './styles';
import { PleaseWaitOverlay } from '@common/components/PleaseWaitOverlay';
import SubmissionFlowFooter from '@common/components/SubmissionFlowFooter/SubmissionFlowFooter';
import { documentTypeToCustomLabelMap, serviceDeskEmail } from '@common/constants';
import { selectConfiguration } from '@common/features/configuration/selectors';
import RequestEpilModal from '@common/features/document/components/RequestEpilModal/RequestEpilModal';
import RequestEpilStandardModal from '@common/features/document/components/RequestEpilStandardModal/RequestEpilStandardModal';
import {
  getDocumentAttributes,
  getDocumentProcessingFlowForDocument,
  resolveContentDetailsDocumentType,
} from '@common/features/document/helpers';
import { useRequestEpilModal } from '@common/features/document/hooks/useRequestEpilModal';
import { useRequestEpilStandardModal } from '@common/features/document/hooks/useRequestEpilStandardModal';
import { ProductGroupFormFields } from '@common/features/productGroup/hooks/useProductGroupForm/types';
import {
  BadgeElementWrapper,
  Card,
  ContentHeaderSummary,
  ContentHeaderWrapper,
  ContentWrapper,
  Description,
  LayoutWrapper,
  MainContentHeaderWrapper,
  ProductGroupWrap,
  Subtitle,
  TableWrapper,
  Title,
} from '@common/features/productGroup/styles';
import { useValidateSubmissionData } from '@common/features/submission/SubmissionTaskDetails/hooks/useValidateSubmissionData';
import { SubmissionStatus } from '@common/services/types';
import { useUpdateSubmissionSubstances } from '../../hooks/useUpdateSubmissionSubstances';
import { useUnableToUpdateAudioVideoModal } from '../UnableToUpdateAudioVideoModal/useUnableToUpdateAudioVideoModal';
import { UnableToUpdateAudioVideoModal } from '../UnableToUpdateAudioVideoModal/UnableToUpdateAudioVideoModal';
import { mapConversionErrorsToErrorLogs } from '@common/hooks/useFetchDocumentConversionErrors';
import {
  DocumentConversionErrorsModal,
  useDocumentConversionErrorsModal,
} from '@common/components/DocumentConversionErrorsModal';
import { selectDocumentConversionResults, selectUserEmail } from '@common/features/user/selectors';
import { RequestEpilQrdFormFields, RequestEpilStandardFormFields } from '@common/features/document/types';
import { useAppSelector } from '@common/hooks/redux';
import { useLockedItemInterception } from '../../hooks/useLockedItemInterception/useLockedItemInterception';

const productsTooltipText = 'We define a product as a combination of Brand/generic name, strength(s) and form';

const errorIcon = '/icons/warning-sign-filled.svg';

interface ISubmissionEditableProductGroup {
  submission: ISubmissionDto;
  isLoading: boolean;
  successCallback: () => void;
}

const SubmissionEditableProductGroup = ({ submission, isLoading, successCallback }: ISubmissionEditableProductGroup) => {
  const location = useLocation();
  const state = location.state as BackToContentTypeState;
  const selectContentType = !!state && state.contentType !== undefined ? state.contentType : DocumentType.Smpc;
  const email = useAppSelector(selectUserEmail);
  const { submissionId = '' } = useParams();
  const {
    goToProduct,
    goToDashboard,
    goToSubmissions,
    goToUpdateDocumentContentDetails,
    goToReadOnlyDocumentContentDetails,
    goToReadOnlyProduct,
    goToProductGroup,
  } = useAppNavigation();

  const { isLockedModalOpen, lockingSubmission, closeLockedModal, blockIfLocked, withLockGuard } = useLockedItemInterception();
  const [selectedTab, setSelectedTab] = useState<string>(ProductGroupTabs.Content);
  const [contentType, setContentType] = useState(selectContentType);
  const { htmlContentModalParams, handleViewContent, closeHtmlContentModal } = useHtmlContentModal();
  const { isUnableToUpdateAudioVideoModalOpen, closeUnableToUpdateAudioVideoModal, openUnableToUpdateAudioVideoModal } =
    useUnableToUpdateAudioVideoModal();

  const queryClient = useQueryClient();

  const { companyConfiguration } = useFetchCompanyConfiguration(submission.companyId);

  const { updateSubmission } = useUpdateSubmission();

  const documents = submission?.documents;
  const products = submission?.products;
  const [isLastProductWarningOpen, setIsLastProductWarningOpen] = useState(false);

  const { tasks } = useFetchTasks({ submissionId });
  const documentConversionResults = useAppSelector(selectDocumentConversionResults);
  const { allDocumentsWithTasks, documentsWithTasksForSubmissionSummary } = mapDocumentsAndTasks(
    tasks,
    documents,
    documentConversionResults,
  );
  const {
    isDocumentConversionErrorsModalOpen,
    isFetchingDocumentConversionErrors,
    documentConversionErrors,
    openDocumentConversionErrorModal,
    closeDocumentConversionErrorModal,
  } = useDocumentConversionErrorsModal(contentType, allDocumentsWithTasks);

  const { prepareDocumentForRetire } = usePrepareDocumentForRetire();
  const { updateSubmissionSubstances } = useUpdateSubmissionSubstances();
  const { modalState, setModalState, closeModal } = useRetireContentModal();
  const { isCancelModalOpen, handleCancelModalConfirm, openCancelModal, closeCancelModal } = useCancelModal({
    confirmRedirect: goToDashboard,
  });

  const handleProductRemoveClick = (productId: string, title: string) => {
    // Block deletion if this is the last product in the submission
    if (submission.products.length === 1) {
      setIsLastProductWarningOpen(true);
      return;
    }
    handleOpenProductRemoveModal(submissionId, productId, title);
  };

  const handleProductDetailsClick = (productId: string) => goToReadOnlyProduct(submissionId, productId);

  const handleDocumentRetireClick = (documentId: string, documentType: DocumentType) => {
    const { requiresRetirementReason } = getDocumentAttributes(documentType, false);

    if (requiresRetirementReason) {
      setModalState({ isOpen: true, documentId });
    } else {
      handleRetire(documentId, documentType);
    }
  };

  const handleDocumentDetailsClick = (documentId: string) => {
    const resolvedType = resolveContentDetailsDocumentType(documents, documentId, contentType);
    //There is no route for customer UI to view epil directly, therefore revert to PIL
    //Components Will decided whether to load epil or pil
    const docType = resolvedType === DocumentType.Epil ? DocumentType.Pil : resolvedType;
    const backToContentState: BackToContentTypeState = { contentType: docType };

    goToReadOnlyDocumentContentDetails(submissionId, docType, documentId, backToContentState);
  };

  const handleRetire = (documentId: string, documentType: DocumentType, reason?: ISelectOption) =>
    prepareDocumentForRetire(
      { submissionId: parseInt(submissionId), documentId, documentType, reason },
      {
        onSuccess: () => {
          const { requiresRetirementReason } = getDocumentAttributes(documentType, false);

          if (requiresRetirementReason) {
            const docType = documentType === DocumentType.Epil ? DocumentType.Pil : documentType;
            const backToContentState: BackToContentTypeState = { contentType: docType ?? contentType };

            goToUpdateDocumentContentDetails(submissionId, contentType, documentId, backToContentState);
          } else {
            successCallback();
          }
        },
      },
    );

  const handleProductUpdateClick = (productId: string) => goToProduct(submissionId, productId);

  const {
    handleCancel: handleProductRemoveCancel,
    handleConfirm: handleProductRemoveConfirm,
    handleOpenModal: handleOpenProductRemoveModal,
    isModalOpen: isProductRemoveModalOpen,
    removeDetails: removeProductDetails,
  } = useRemoveModal({ type: 'product', onSuccess: () => successCallback() });

  const {
    handleCancel: handleDocumentRemoveCancel,
    handleConfirm: handleDocumentRemoveConfirm,
    handleOpenModal: handleOpenDocumentRemoveModal,
    isModalOpen: isDocumentRemoveModalOpen,
    removeDetails: removeDocumentDetails,
  } = useRemoveModal({ type: 'document', onSuccess: () => successCallback() });

  const {
    productIdToDiscontinue,
    isDiscontinuingProduct,
    handleDiscontinueProductCancel,
    handleDiscontinueProductConfirm,
    handleDiscontinueClick,
  } = useDiscontinueProductModal({ onDiscontinueSuccess: (productId) => goToProduct(submissionId, productId) });

  const { handleRetireProductCancel, handleRetireProductConfirm, handleRetireClick, productIdToRetire, isRetiringProduct } =
    useRetireProductModal({ onRetireSuccess: () => successCallback() });

  const { productIdToReinstate, handleReinstateProductCancel, handleReinstateProductConfirm, handleReinstatementClick } =
    useReinstatementProductModal({ onReinstatementSuccess: () => successCallback() });

  const findLockedProduct = (productId: string) => products?.find((p) => p.productId.toString() === productId);

  const lockedGroupMetadataItem =
    products?.find((p) => p.isLockedByOtherSubmission) ?? documents?.find((d) => d.isLockedByOtherSubmission);
  const isGroupMetadataLocked = !!lockedGroupMetadataItem;

  const tableCallbacks: IProductTableCallbacks = {
    onRemoveClick: withLockGuard(findLockedProduct, handleProductRemoveClick),
    onUpdateClick: withLockGuard(findLockedProduct, handleProductUpdateClick),
    onDiscontinueClick: withLockGuard(findLockedProduct, handleDiscontinueClick),
    onDetailsClick: handleProductDetailsClick,
    onReinstatementClick: withLockGuard(findLockedProduct, handleReinstatementClick),
    onRetirementClick: withLockGuard(findLockedProduct, handleRetireClick),
  };

  const columns = prepareProductsColumns(true, tableCallbacks);
  const {
    methods,
    formState: { isDirty, errors },
    handleReset,
    watch,
    trigger,
    getValues,
    clearErrors: clearFormErrors,
    showProductTypeWarning,
  } = useProductGroupForm({
    productType: submission?.productGroup.productType,
    isSameAsSubmitterCompany: !!submission?.productGroup.maHolderCompanyId,
    companyName: submission?.productGroup.maHolderCompanyName,
    substances: submission?.productGroup.substances,
  });

  const {
    isValidateOpen,
    setIsValidateOpen,
    handleCancelValidationModal,
    handleProductGroupErrorClick,
    handleProductErrorClick,
    handleContentErrorClick,
    handleProductAssociationErrorClick,
    handleContentAssociationErrorClick,
  } = useValidateSubmissionModal(documents ?? [], (errorLog) => {
    if (errorLog.subError === 'MA Holder') setSelectedTab(ProductGroupTabs.MaHolder);
    if (errorLog.subError === 'Product Type') setSelectedTab(ProductGroupTabs.ProductType);
  });

  const {
    submissionSummaryMethods,
    showSummary,
    saveInProgress,
    handleConfirmationClose,
    setShowSummary,
    showConfirmation,
    handleSummaryCancel,
    handleFileOpen,
    handleSummarySubmit,
    contentWithSameProductGroupsModalState,
    handleContentWithSameProductGroupsModalClose,
  } = useSubmissionSummary({
    submissionId,
    onSubmitSuccess: () => successCallback(),
  });

  const configuration = useAppSelector(selectConfiguration);
  const cmsLink = configuration.cmsLink;

  const emailExists = (): boolean => {
    return !email ? false : email.trimEnd().length > 0;
  };

  const handleSuccessValidation = () => {
    setShowSummary(true);
    successCallback();
  };

  const { validationErrors, validateSubmissionData } = useValidateSubmissionData({
    submissionId,
    handleErrors: () => setIsValidateOpen(true),
    handleSuccess: handleSuccessValidation,
  });

  const { handleUnsavedChanges } = useUnsavedChanges({
    shouldRenderModal: isDirty,
    title: 'You currently have unsaved changes.\nAre you sure you want to exit without saving?\nAny changes will be lost.',
  });

  const {
    isVisible,
    isSameAsSubmitterCompany,
    showMaHolderWarning,
    companyName,
    companyNameError,
    handleSameAsSubmitterCompanyChange,
    handleCompanyNameChange,
  } = useMaHolder(methods);

  const isContentTabSelected = selectedTab && selectedTab === ProductGroupTabs.Content;

  const showSmpcPilOption = !documents?.some(
    (d) => d.status === DocumentStatus.Draft && [DocumentType.Smpc, DocumentType.Pil].includes(d.type),
  );

  const showProductOption = !products?.some((p) => p.status === ProductStatus.Draft);

  const handleSubmissionUpdateSuccess = () => {
    setSelectedTab(ProductGroupTabs.Content);
    handleReset(true);
    queryClient.invalidateQueries([queryKey]);
  };

  const addProductSuccessCallback = (productId: number) => goToProduct(submissionId, productId);

  const {
    isProductModalOpen,
    duplicateProduct,
    conflictField,
    activeIngredientsNames,
    isCreatingProduct,
    handleProductPopupOpen,
    handleProductCreate,
    handleDuplicateErrorClose,
    handleProductView,
    handleProductModalClose,
    setProductIsOpen,
  } = useAddProduct({ submission, handleSuccess: addProductSuccessCallback });

  const {
    handleFileSubmit,
    handleUploadClick,
    uploadErrors,
    handleRemove: handleFileRemove,
    handleCancel: handleDocumentAddCancel,
    handleOpenModal: handleDocumentAddModalOpen,
    clearErrors: clearDocumentAddErrors,
    uploadProgress: documentUploadProgress,
    isModalOpen: isAddDocumentModalOpen,
    isLoading: isFileLoading,
    showMissingProducts,
    fileErrors,
    productsMatchingDocument,
    isChangingDocumentAssociation,
    secondaryDocument,
    fileUploadFormMethods,
    readonlyFields,
  } = useAddDocumentModal({
    contentType,
    submissionId,
    productGroupId: submission?.productGroupId ?? null,
    products: submission?.products,
    existingDocuments: submission?.documents,
    companyConfiguration: companyConfiguration,
    onLockedProductSelected: blockIfLocked,
  });

  const handleAddDocumentClick = () => {
    const backToContentState: BackToContentTypeState = { contentType };

    handleUploadClick(backToContentState);
  };

  const handleWrapperProductErrorClick = (id: number) => {
    const backToContentState: BackToContentTypeState = { contentType };

    handleProductErrorClick(id, backToContentState);
  };

  const handleWrapperContentErrorClick = (id: number) => {
    const backToContentState: BackToContentTypeState = { contentType };

    handleContentErrorClick(id, backToContentState);
  };

  const handleRequestEpilModalUploadSuccess = () => {
    queryClient.invalidateQueries([queryKey]);
  };

  const handleRequestEpilStandardModalUploadSuccess = (documentId: string) => {
    const document = submission.documents.find((d) => d.documentId === parseInt(documentId));
    const backToContentTypeState: BackToContentTypeState = { contentType: DocumentType.Pil };

    goToReadOnlyDocumentContentDetails(
      submission.submissionId.toString(),
      document?.type!,
      document?.documentId.toString()!,
      backToContentTypeState,
    );
  };

  const {
    handleRequestEpilModalFileSubmit,
    handleRequestEpilModalUploadClick,
    handleRequestEpilModalCancel,
    handleRequestEpilModalRemove,
    handleRequestEpilModalOpen,
    clearRequestEpilModalErrors,
    handleAlternativeTextFileSubmit,
    handleAlternativeTextFileRemove,
    alternativeTextFileUploadProgress,
    isAlternativeTextFileBeingUploaded,
    requestEpilModalFileErrors,
    isRequestEpilModalOpen,
    isRequestEpilModalLoading,
    requestEpilModalUploadProgress,
    requestEpilModalMethods,
  } = useRequestEpilModal(handleRequestEpilModalUploadSuccess);

  const {
    handleRequestEpilStandardModalFileSubmit,
    handleRequestEpilStandardModalUploadClick,
    handleRequestEpilStandardModalCancel,
    handleRequestEpilStandardModalRemove,
    handleRequestEpilStandardModalOpen,
    clearRequestEpilStandardModalErrors,
    requestEpilStandardModalFileErrors,
    isRequestEpilStandardModalOpen,
    isRequestEpilStandardModalLoading,
    requestEpilStandardModalUploadProgress,
    requestEpilStandardModalMethods,
  } = useRequestEpilStandardModal(handleRequestEpilStandardModalUploadSuccess);

  const {
    isNextStepsOpen,
    isDontShowAgainChecked,
    handleDontShowAgainCheck,
    handleNextStepsClose,
    handleProductGetStarted,
    handleSmpcGetStarted,
    handlePilGetStarted,
  } = useNextStepsModal({
    initialShowModal: (showSmpcPilOption || showProductOption) && !isLoading,
    onProductGetStarted: () => setProductIsOpen(true),
    onSmpcGetStarted: () => handleDocumentAddModalOpen(submission?.productGroup.productGroupName!),
    onPilGetStarted: () => {
      setContentType(DocumentType.Pil);
      handleDocumentAddModalOpen(submission?.productGroup.productGroupName!);
    },
  });

  const errorTitle = `There was an issue uploading your ${documentTypeToCustomLabelMap[contentType]}`;
  const requestEpilErrorTitle = `There was an issue uploading your ${documentTypeToCustomLabelMap[DocumentType.Epil]}`;

  const handleClose = () => clearDocumentAddErrors();

  const handleRequestEpilErrorsModalClose = () => clearRequestEpilModalErrors();

  const handleRequestEpilStandardErrorsModalClose = () => clearRequestEpilStandardModalErrors();

  const productType = watch(ProductGroupFormFields.productType)?.value;

  const handleSaveChanges = async () => {
    const isValid = await trigger();
    if (
      selectedTab !== ProductGroupTabs.MaHolder &&
      selectedTab !== ProductGroupTabs.Content &&
      selectedTab !== ProductGroupTabs.ProductType &&
      selectedTab !== ProductGroupTabs.ActiveIngredients
    ) {
      clearFormErrors(ProductGroupFormFields.maHolder);
    }

    const currentErrors = methods.formState.errors;

    if (
      (selectedTab === ProductGroupTabs.MaHolder && currentErrors.maHolder) ||
      (selectedTab === ProductGroupTabs.ProductType && currentErrors.productType) ||
      (selectedTab === ProductGroupTabs.ActiveIngredients &&
        getValues(ProductGroupFormFields.activeIngredients).length === 0 &&
        getValues(ProductGroupFormFields.noActiveIngredientsCheckbox) === false)
    ) {
      return;
    }

    if (selectedTab === ProductGroupTabs.ActiveIngredients) {
      const activeIngredients = getValues(ProductGroupFormFields.activeIngredients);
      updateSubmissionSubstances({ submissionId, payload: { activeIngredients } }, { onSuccess: handleSubmissionUpdateSuccess });

      return;
    }

    const payload: SubmissionUpdateCommand = {
      productType: productType ?? '',
      submissionMetadata: {
        maHolder: {
          isSameAsSubmitterCompany: !!isSameAsSubmitterCompany,
          companyName,
        },
      },
    };

    updateSubmission({ submissionId, payload }, { onSuccess: handleSubmissionUpdateSuccess });
  };

  const handleTabSelected = (label: string) =>
    handleUnsavedChanges(() => {
      setSelectedTab(label);
      handleReset();
    });

  const handleProductPopupOpenClick = () =>
    handleUnsavedChanges(() => {
      handleProductPopupOpen();
      handleReset();
    });

  const handleCancel = () => {
    if (isContentTabSelected) {
      return openCancelModal();
    }
    handleUnsavedChanges(() => {
      setSelectedTab(ProductGroupTabs.Content);
      handleReset();
    });
  };

  const handleRequestEpil = (documentId: string) => {
    if (companyConfiguration?.hasQrdEpilsEnabled) {
      handleRequestEpilModalOpen(documentId);
    } else {
      handleRequestEpilStandardModalOpen(documentId);
    }
  };

  const substances = submission?.productGroup.substances;
  const activeIngredients = getSubstanceNames(substances);

  const isActiveIngredientsTabEditable =
    !submission.productGroupId &&
    (submission.status === SubmissionStatus.Draft || submission.status === SubmissionStatus.InProgress);

  //If a submission only consists of Document Assignments, then there will be no Product Changes,
  //state will be unchanged and the Summary Screen will appear empty.
  //Therefore updated products has been modified to also consider all Products that have the
  //status of draft.
  const updatedProducts = Array.from(
    new Set(
      submission?.products.filter(
        (p) =>
          p.changeType !== ProductChangeType.Unchanged ||
          p.status === ProductStatus.Draft ||
          p.linkedDocumentIds.filter((v) => allDocumentsWithTasks.map((o) => o.documentId).includes(v)).length > 0,
      ) ?? [],
    ),
  );

  const productToReinstateName = productIdToReinstate
    ? products?.find((x) => x.productId.toString() === productIdToReinstate.toString())!.name
    : '';

  const productToRetireName = productIdToRetire
    ? products?.find((x) => x.productId.toString() === productIdToRetire.toString())!.name
    : '';

  const handleRetrySummarySubmit = async () => {
    await handleSummarySubmit(email);
  };

  const onContentWithSameProductGroupsConfirm = () => {
    handleContentWithSameProductGroupsModalClose();
  };

  return showSummary ? (
    <FormProvider {...submissionSummaryMethods}>
      <SubmissionSummary
        submissionId={submissionId}
        submissionTitle={submission?.title ?? ''}
        submissionStatus={submission?.status!}
        pleaseWait={saveInProgress}
        tasks={documentsWithTasksForSubmissionSummary ?? []}
        productGroup={mapProductGroup(submission)}
        products={updatedProducts}
        onCancel={openCancelModal}
        onFileOpen={handleFileOpen}
        onSubmit={() => handleSummarySubmit(email)}
        onBackClick={handleSummaryCancel}
        onViewContent={handleViewContent}
      />
      <HtmlContentModal modalParams={htmlContentModalParams} onClose={() => closeHtmlContentModal()} />
      <NotifyModal
        confirmText={'Retry'}
        onClose={handleConfirmationClose}
        onConfirm={handleRetrySummarySubmit}
        title={'There was an issue with your submission'}
        isOpen={showConfirmation === SubmissionConfirmationType.Failure}
        width={emailExists() ? '32rem' : '36rem'}
      >
        {!emailExists() && (
          <p>
            You don't have an email address associated to your account, please update your email using our&nbsp;
            <a target="_blank" rel="noreferrer" href={cmsLink}>
              Contact Management System (CMS)
            </a>
            &nbsp; If you continue to experience problems submitting, please contact{' '}
            <a href={`mailto:${serviceDeskEmail}`}>{serviceDeskEmail}</a>
          </p>
        )}
        {emailExists() && (
          <p>
            Please retry, if you continue to experience problems submitting, please contact&nbsp;
            <a href={`mailto:${serviceDeskEmail}`}>{serviceDeskEmail}</a>
          </p>
        )}
      </NotifyModal>
      <NotifyModal
        noIcon={true}
        onConfirm={() => {
          handleConfirmationClose();
          successCallback && successCallback();
        }}
        isOpen={showConfirmation === SubmissionConfirmationType.Successfull}
        width="32rem"
      >
        <h3>
          Thank you for your submission, you can review its progress{' '}
          <Link
            style={{ cursor: 'pointer' }}
            onClick={() => {
              goToSubmissions();
            }}
          >
            here
          </Link>
        </h3>
      </NotifyModal>

      <NotifyModal
        isOpen={contentWithSameProductGroupsModalState.isOpen}
        onConfirm={onContentWithSameProductGroupsConfirm}
        title="Content associated to more than one product group"
        width="700px"
      >
        Some of the content being updated in this submission is associated to more than one product group, updating this content
        will not update the content on the following product groups:
        <Styled.SameContentModalText>
          <ul>
            {contentWithSameProductGroupsModalState.productGroupsNames.map((productGroupName) => (
              <li>{productGroupName}</li>
            ))}
          </ul>
        </Styled.SameContentModalText>
        A draft submission has been created for these product groups which you can access from your submissions.
      </NotifyModal>

      <CancelCreateSubmissionModal isOpen={isCancelModalOpen} onClose={closeCancelModal} onConfirm={handleCancelModalConfirm} />
    </FormProvider>
  ) : (
    <>
      <LayoutWrapper>
        <SubmissionFlowHeader
          submissionTitle={submission?.title ?? ''}
          submissionStatus={submission?.status!}
          title={submission?.productGroup.productGroupName}
          activeIngredients={activeIngredients}
          productGroupId={submission?.productGroupId ?? null}
        >
          <HeaderBreadcrumb>
            <Link>Product Family</Link> &gt; Product Group
          </HeaderBreadcrumb>
        </SubmissionFlowHeader>
        <ProductGroupWrap>
          <Card>
            <MainContentHeaderWrapper>
              <ContentHeaderSummary>
                <Title>Product group management</Title>
                <Description>This is where you can add products and content that relate to this product group</Description>
              </ContentHeaderSummary>
            </MainContentHeaderWrapper>
            <FormProvider {...methods}>
              <Tabs
                selectedTab={selectedTab}
                onTabSelected={handleTabSelected}
                items={[
                  {
                    label: ProductGroupTabs.Content,
                    content: (
                      <ContentTab
                        documents={allDocumentsWithTasks ?? []}
                        onContentAdd={(productGroupName, documentId, mode, documentType) =>
                          handleDocumentAddModalOpen(productGroupName, documentId, undefined, undefined, mode, documentType)
                        }
                        onContentRemove={(documentId: string, title: string) =>
                          handleOpenDocumentRemoveModal(submissionId, documentId, title)
                        }
                        onContentRetire={handleDocumentRetireClick}
                        onContentTypeSelect={(contentType) => setContentType(contentType)}
                        onContentDetailsClick={handleDocumentDetailsClick}
                        onLockedItemClick={blockIfLocked}
                        onRequestEpil={handleRequestEpil}
                        showAudioVideoUpdateWarning={openUnableToUpdateAudioVideoModal}
                        contentType={contentType}
                        onViewErrorsClick={openDocumentConversionErrorModal}
                      />
                    ),
                  },
                  {
                    label: ProductGroupTabs.ActiveIngredients,
                    content: (
                      <ActiveIngredientsTab
                        isEditable={isActiveIngredientsTabEditable}
                        activeIngredientsNames={activeIngredientsNames}
                        activeIngredientsFieldName={ProductGroupFormFields.activeIngredients}
                        noActiveIngredientsCheckboxFieldName={ProductGroupFormFields.noActiveIngredientsCheckbox}
                      />
                    ),
                    'data-testid': 'active-ingredients-tab',
                  },
                  {
                    label: ProductGroupTabs.MaHolder,
                    content: (
                      <MaHolderTab
                        isVisible={isVisible}
                        isSameAsSubmitterCompany={isSameAsSubmitterCompany}
                        companyName={companyName}
                        companyNameError={companyNameError}
                        showWarning={showMaHolderWarning}
                        editable={!isGroupMetadataLocked}
                        onSameAsSubmitterCompanyChange={handleSameAsSubmitterCompanyChange}
                        onCompanyNameChange={handleCompanyNameChange}
                        onUnmount={handleReset}
                      />
                    ),
                    icon: showMaHolderWarning ? errorIcon : undefined,
                    'data-testid': 'ma-holder-tab',
                  },
                  {
                    label: ProductGroupTabs.ProductType,
                    content: (
                      <ProductTypeTab
                        editable={!isGroupMetadataLocked}
                        value={productType ?? submission?.productGroup.productType}
                      />
                    ),
                    icon: showProductTypeWarning ? errorIcon : undefined,
                    'data-testid': 'product-type-tab',
                  },
                ]}
              />
            </FormProvider>
          </Card>
          <Card>
            <ContentWrapper>
              <ContentHeaderWrapper>
                <ContentHeaderSummary>
                  <BadgeElementWrapper>
                    <Subtitle>Products</Subtitle>
                    <IconWithTooltip tooltipId="emc" iconSrc="/icons/info_circle.svg" tooltipText={productsTooltipText} />
                    <Badge value={submission?.products.length} data-testid="products-counter" />
                  </BadgeElementWrapper>
                  <Description>Products can only have one SmPC or PIL associated to them at a time.</Description>
                </ContentHeaderSummary>
                <Button
                  onClick={handleProductPopupOpenClick}
                  text="Add Product"
                  type={ButtonTypes.PRIMARY_MEDIUM_BORDER}
                  icon="/icons/plus-sign.svg"
                  width={150}
                />
              </ContentHeaderWrapper>
              <TableWrapper data-testid="products-table">
                {!submission?.products.length || submission?.products.length === 0 ? (
                  <NoContentPanel
                    title="No products found"
                    helpMessage="How do I add products?"
                    helpLink={process.env.REACT_APP_HELP_PRODUCT_URL}
                  />
                ) : (
                  <DatapharmTable documents={submission?.products ?? []} columns={columns} data-testid="products-table" />
                )}
              </TableWrapper>
              {isProductModalOpen && (
                <AddProductPopup
                  isOpen={isProductModalOpen}
                  substances={substances ?? []}
                  productGroup={{
                    id: submission?.productGroupId || null,
                    name: submission?.productGroup.productGroupName || '',
                    productType: submission?.productGroup.productType || '',
                  }}
                  isAddingProduct={isCreatingProduct}
                  handleCreate={handleProductCreate}
                  handleClose={handleProductModalClose}
                />
              )}
              <DuplicateProductError
                mode={DuplicateProductErrorMode.Create}
                isOpen={!!duplicateProduct}
                productName={duplicateProduct ?? ''}
                onClose={handleDuplicateErrorClose}
                onProductView={handleProductView}
                conflictField={conflictField}
              />
              <RemoveModal
                isOpen={isProductRemoveModalOpen}
                onCancelClick={handleProductRemoveCancel}
                onConfirmClick={handleProductRemoveConfirm}
                type={'product'}
                title={removeProductDetails.title}
              />
            </ContentWrapper>
          </Card>
        </ProductGroupWrap>
      </LayoutWrapper>
      <SubmissionFlowFooter
        onContinue={isContentTabSelected ? validateSubmissionData : handleSaveChanges}
        onCancel={handleCancel}
        continueText={isContentTabSelected ? 'Continue' : 'Save Changes'}
      />

      <NotifyModal
        isOpen={isValidateOpen}
        title="Please add the following missing information"
        onClose={handleCancelValidationModal}
        onConfirm={handleHelpClick}
        confirmText="Help"
        width="50rem"
      >
        <ValidateSubmissionErrors
          onCancel={handleCancelValidationModal}
          validationErrors={validationErrors!}
          onProductGroupErrorClick={handleProductGroupErrorClick}
          onProductErrorClick={handleWrapperProductErrorClick}
          onContentErrorClick={handleWrapperContentErrorClick}
          onProductAssociationErrorClick={handleProductAssociationErrorClick}
          onContentAssociationErrorClick={handleContentAssociationErrorClick}
        />
      </NotifyModal>

      <DocumentConversionErrorsModal
        isOpen={isDocumentConversionErrorsModalOpen}
        contentType={contentType}
        documentConversionErrors={mapConversionErrorsToErrorLogs(documentConversionErrors)}
        showLoader={isFetchingDocumentConversionErrors}
        onCloseClick={closeDocumentConversionErrorModal}
      />

      {isNextStepsOpen && (
        <NextStepsModal
          isDontShowAgainChecked={isDontShowAgainChecked}
          onClose={handleNextStepsClose}
          onDontShowAgainCheck={handleDontShowAgainCheck}
        >
          {showSmpcPilOption && (
            <Item
              title="Add a new SmPC"
              linkText="How to add an SmPC or PIL?"
              onClick={handleSmpcGetStarted}
              helpLink={process.env.REACT_APP_HELP_CONTENT_URL}
              additionalInfo="Use this option if you are uploading a new document or replacing a combined SmPC with an Individual SmPC"
            />
          )}
          {showSmpcPilOption && (
            <Item
              title="Add a new PIL"
              linkText="How to add an SmPC or PIL?"
              onClick={handlePilGetStarted}
              helpLink={process.env.REACT_APP_HELP_CONTENT_URL}
              additionalInfo="Use this option if you are uploading a new document or replacing a combined PIL with an Individual PIL"
            />
          )}
          {showSmpcPilOption && (
            <Item
              title="Update an existing SmPC/PIL"
              linkText="How to add an SmPC or PIL?"
              onClick={handleNextStepsClose}
              helpLink={process.env.REACT_APP_HELP_CONTENT_URL}
              additionalInfo="Use this option if you are uploading an amendment to an existing document"
            />
          )}
          {showProductOption && (
            <Item
              title="Add a Product"
              linkText="How to add or update a Product?"
              helpLink={process.env.REACT_APP_HELP_PRODUCT_URL}
              onClick={handleProductGetStarted}
              additionalInfo="Use this option if you are adding a completely new product"
            />
          )}
          {showProductOption && (
            <Item
              title="Update a Product"
              linkText="How to add or update a Product?"
              helpLink={process.env.REACT_APP_HELP_PRODUCT_URL}
              onClick={handleNextStepsClose}
              additionalInfo="'Use this option if you are making an amendment to an existing product"
            />
          )}
        </NextStepsModal>
      )}
      {productIdToDiscontinue && (
        <DiscontinueProductModal
          isLoading={isDiscontinuingProduct}
          onCancel={handleDiscontinueProductCancel}
          onConfirm={handleDiscontinueProductConfirm}
        />
      )}
      {productIdToRetire && (
        <RetireProductModal
          isLoading={isRetiringProduct}
          onCancel={handleRetireProductCancel}
          onConfirm={handleRetireProductConfirm}
          productName={productToRetireName}
        />
      )}
      {productIdToReinstate && (
        <ReinstatementProductModal
          onCancel={handleReinstateProductCancel}
          onConfirm={handleReinstateProductConfirm}
          productName={productToReinstateName}
        />
      )}
      {isAddDocumentModalOpen && companyConfiguration && (
        <FormProvider {...fileUploadFormMethods}>
          <AddDocumentModal
            type={contentType}
            uploadErrors={uploadErrors}
            productsMatchingDocument={productsMatchingDocument}
            isOpen={isAddDocumentModalOpen}
            isUploading={isFileLoading}
            uploadProgress={documentUploadProgress}
            secondaryDocumentProps={secondaryDocument}
            readonlyFields={readonlyFields}
            documentProcessingFlow={getDocumentProcessingFlowForDocument(contentType, companyConfiguration)}
            onFileSubmit={handleFileSubmit}
            onUploadClick={handleAddDocumentClick}
            onCancelClick={handleDocumentAddCancel}
            onRemove={handleFileRemove}
          />
          {isChangingDocumentAssociation && <PleaseWaitOverlay />}
        </FormProvider>
      )}
      {isRequestEpilModalOpen && companyConfiguration && (
        <FormProvider {...requestEpilModalMethods}>
          <RequestEpilModal
            isOpen={isRequestEpilModalOpen}
            isUploading={isRequestEpilModalLoading || isAlternativeTextFileBeingUploaded}
            uploadProgress={requestEpilModalUploadProgress}
            alternativeTextFileFormField={RequestEpilQrdFormFields.alternativeTextFile}
            alternativeTextCheckboxFormField={RequestEpilQrdFormFields.isWithAlternativeText}
            alternativeTextFileUploadProgress={alternativeTextFileUploadProgress}
            documentProcessingFlow={getDocumentProcessingFlowForDocument(DocumentType.Epil, companyConfiguration)}
            onFileSubmit={handleRequestEpilModalFileSubmit}
            onUploadClick={handleRequestEpilModalUploadClick}
            onCancelClick={handleRequestEpilModalCancel}
            onAlternativeTextFileRemove={handleAlternativeTextFileRemove}
            onAlternativeTextFileSubmit={handleAlternativeTextFileSubmit}
            onRemove={handleRequestEpilModalRemove}
          />
        </FormProvider>
      )}
      {isRequestEpilStandardModalOpen && companyConfiguration && (
        <FormProvider {...requestEpilStandardModalMethods}>
          <RequestEpilStandardModal
            isOpen={isRequestEpilStandardModalOpen}
            isUploading={isRequestEpilStandardModalLoading}
            uploadProgress={requestEpilStandardModalUploadProgress}
            alternativeTextFileFormField={RequestEpilStandardFormFields.alternativeTextFile}
            alternativeTextCheckboxFormField={RequestEpilStandardFormFields.isWithAlternativeText}
            documentProcessingFlow={getDocumentProcessingFlowForDocument(DocumentType.Epil, companyConfiguration)}
            onFileSubmit={handleRequestEpilStandardModalFileSubmit}
            onUploadClick={handleRequestEpilStandardModalUploadClick}
            onCancelClick={handleRequestEpilStandardModalCancel}
            onRemove={handleRequestEpilStandardModalRemove}
          />
        </FormProvider>
      )}
      <ModalWrapper isOpen={modalState.isOpen}>
        <RetireModal
          onConfirmClick={handleRetire}
          submission={submission}
          onCancelClick={closeModal}
          modalState={modalState}
          contentType={contentType}
        />
      </ModalWrapper>
      <RemoveModal
        isOpen={isDocumentRemoveModalOpen}
        onCancelClick={handleDocumentRemoveCancel}
        onConfirmClick={handleDocumentRemoveConfirm}
        type={'document'}
        title={removeDocumentDetails.title}
      />
      <NotifyModal title={errorTitle} isOpen={fileErrors.length > 0} width="32rem">
        <FileError onClose={handleClose} errors={fileErrors} type={contentType} />
      </NotifyModal>

      <NotifyModal title={requestEpilErrorTitle} isOpen={requestEpilModalFileErrors.length > 0} width="32rem">
        <FileError onClose={handleRequestEpilErrorsModalClose} errors={requestEpilModalFileErrors} />
      </NotifyModal>

      <NotifyModal title={requestEpilErrorTitle} isOpen={requestEpilStandardModalFileErrors.length > 0} width="32rem">
        <FileError onClose={handleRequestEpilStandardErrorsModalClose} errors={requestEpilStandardModalFileErrors} />
      </NotifyModal>

      <NotifyModal title={'Missing Products'} onClose={handleClose} isOpen={showMissingProducts} width="32rem">
        <p>At least one product must be specified</p>
      </NotifyModal>
      {isLastProductWarningOpen && (
        <NotifyModal
          isOpen={isLastProductWarningOpen}
          title="Unable to remove product"
          onClose={() => setIsLastProductWarningOpen(false)}
          onConfirm={() => setIsLastProductWarningOpen(false)}
          confirmText="Confirm"
          modalType={ModalTypes.error}
        >
          You cannot delete the last product in a submission. If you would like to remove the last product association, please
          cancel the submission and retire the document.
        </NotifyModal>
      )}
      <CancelCreateSubmissionModal isOpen={isCancelModalOpen} onClose={closeCancelModal} onConfirm={handleCancelModalConfirm} />
      <UnableToUpdateAudioVideoModal
        isOpen={isUnableToUpdateAudioVideoModalOpen}
        onConfirm={closeUnableToUpdateAudioVideoModal}
      />
      <NotifyModal
        isOpen={isLockedModalOpen}
        title="Conflict with another submission"
        onClose={closeLockedModal}
        cancelText="Close"
        modalType={ModalTypes.error}
        width="36rem"
      >
        <p>
          An in progress{' '}
          {lockingSubmission?.submissionId ? (
            <Link
              style={{ cursor: 'pointer' }}
              onClick={() => {
                closeLockedModal();
                goToProductGroup(lockingSubmission.submissionId!.toString());
              }}
            >
              submission{' '}
            </Link>
          ) : (
            'submission'
          )}
          already exists for this product - You can request changes to this submission via the submissions screen.
        </p>
      </NotifyModal>
    </>
  );
};

export default SubmissionEditableProductGroup;
